import {
  DOWN_KEYCODE,
  ENTER_KEYCODE,
  ESC_KEYCODE,
  SelectStateContext,
  SPACE_KEYCODE,
  UP_KEYCODE,
} from 'app/components/forms/SelectState/constants';
import { eventHasModifierKeys } from 'app/utils/env';
import { OnFocusOutside } from 'app/utils/react';
import React, {
  ReactElement,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

interface ISelectWrapperControlProps {
  onClick: (event: React.MouseEvent) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  onKeyPress: (event: React.KeyboardEvent) => void;
  ref: (node: HTMLElement | null) => void;
  role: string;
  tabIndex: number;
}

interface ISelectWrapperProps {
  children: (props: ISelectWrapperControlProps) => ReactElement<any> | null;
  // Components should not take RefObjects as props, because they won't necessarily be notified if
  // the ref-ed element changes. Instead, this should somehow provide a ref callback to the caller
  // which it can assign to the appropriate `ref={...}` prop.
  labelRef?: RefObject<HTMLElement>;
}

/**
 * `<SelectWrapper>` is responsible for:
 *
 * 1. Triggering `SelectState.open` when it gets clicked or on enter/space/up/down key down
 * 2. Triggering `SelectState.close` when it gets unfocused
 * 3. Causing its DOM element to get `.focus()`ed when `SelectState.close` is called with
 *    `focusWrapper: true`
 */
const SelectWrapper = ({ children, labelRef }: ISelectWrapperProps) => {
  const {
    close,
    focusWrapperTrigger,
    isOpen,
    open,
    query,
    setQuery,
  } = useContext(SelectStateContext);

  const ref = useRef<HTMLElement | null>(null);
  const callbackRef = useCallback((node: HTMLElement | null) => {
    ref.current = node;
  }, []);

  const focusWrapper = () => {
    if (ref.current && focusWrapperTrigger > 0) {
      ref.current.focus();
    }
  };

  // Bind event to the current label ref if there is one available at the time
  // of render.
  useEffect(() => {
    const currentLabelRef = labelRef && labelRef.current;

    if (currentLabelRef) {
      currentLabelRef.addEventListener('click', focusWrapper);

      return () => {
        currentLabelRef.removeEventListener('click', focusWrapper);
      };
    }
  });

  useEffect(focusWrapper, [focusWrapperTrigger]);

  return (
    <React.Fragment>
      {
        // Render an invisible button before the select menu, to handle the user clicking on a
        // <label> element wrapping the select menu, and focus the select menu.
        //
        // We don't use <BaseButton> here <SelectState> will hopefully be a separate library one
        // day (and we don't need anything that <BaseButton> provides in this case)
      }
      {/* eslint-disable-next-line react/forbid-elements */}
      <button type="button" style={{ display: 'none' }} onClick={focusWrapper} aria-hidden="true" />
      <OnFocusOutside handler={() => close(false)} getNode={() => ref.current} />
      {children({
        onClick: (event: React.MouseEvent) => {
          if (!isOpen) {
            event.preventDefault();
            open();
          }
        },
        onKeyDown: (event: React.KeyboardEvent) => {
          if (!eventHasModifierKeys(event)) {
            const { keyCode } = event;

            if (isOpen && keyCode === ESC_KEYCODE) {
              event.preventDefault();
              close(true);
            } else if (
              !isOpen &&
              (
                keyCode === SPACE_KEYCODE ||
                keyCode === ENTER_KEYCODE ||
                keyCode === UP_KEYCODE ||
                keyCode === DOWN_KEYCODE
              )
            ) {
              event.preventDefault();
              open();
            }
          }
        },
        onKeyPress: (event: React.KeyboardEvent) => {
          if (event.key && event.key.length === 1) {
            setQuery(query + event.key);
          }
        },
        ref: callbackRef,
        role: 'listbox',
        // Don't allow focusing the wrapper when the menu is open. Only the options should be
        // focused while open.
        tabIndex: isOpen ? -1 : 0,
      })}
    </React.Fragment>
  );
};

export default SelectWrapper;
